import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	ContentChild,
	ElementRef,
	inject,
	input,
	PLATFORM_ID,
	signal,
	ViewChild,
} from '@angular/core';
import { Params, RouterModule } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { TranslatePipe } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { MarkdownModule } from 'ngx-markdown';
import { Observable } from 'rxjs';

import { LinkListComponent } from '@valk-nx/components/ui-link-list/src/lib/link-list.component';
import { PricingComponent } from '@valk-nx/components/ui-pricing/src/lib/pricing';
import { RoomFacilitiesComponent } from '@valk-nx/components/ui-room-facilities/src/lib/room-facilities.component';
import { SocialProofComponent } from '@valk-nx/components/ui-social-proof/src/lib/social-proof';
import { TagComponent } from '@valk-nx/components/ui-tag/src/lib/tag';
import { SmartLinkComponent } from '@valk-nx/core/lib/components/smart-link/smart-link.component';
import { cn, LinkTarget } from '@valk-nx/core/lib/core';
import { HeadingType } from '@valk-nx/core/lib/directives/heading/heading.type';
import { SocialProofInterface } from '@valk-nx/core/lib/interfaces/hotels.interface';
import { PhotoWithDimensionsInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { PriceInterface } from '@valk-nx/core/lib/interfaces/price.interface';
import { PricingTooltip } from '@valk-nx/helpers/lib/pricing-tooltip/pricing-tooltip.interface';
import { RoomCardFacilitiesInterface } from '@valk-nx/rooms-store/rooms.interface';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

import { PersuadeCardHeaderComponent } from './sub-components/header/persuade-card-header.component';
import { PersuadeCardMediaComponent } from './sub-components/media/persuade-card-media.component';
import { FooterContentDirective } from './persuade-card.directive';
import { LinkType, PersuadeCardLinkList } from './persuade-card.interface';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: { ngSkipHydration: 'true' },
	selector: 'vp-persuade-card',
	templateUrl: './persuade-card.component.html',
	imports: [
		AngularSvgIconModule,
		CommonModule,
		LetDirective,
		LinkListComponent,
		MarkdownModule,
		PersuadeCardHeaderComponent,
		PersuadeCardMediaComponent,
		RoomFacilitiesComponent,
		RouterModule,
		SmartLinkComponent,
		PricingComponent,
		SocialProofComponent,
		TagComponent,
		TranslatePipe,
	],
})
export class PersuadeCardComponent implements AfterViewInit {
	private readonly platformId = inject(PLATFORM_ID);
	readonly cd = inject(ChangeDetectorRef);
	private readonly viewport = inject(ViewPortService);

	// Image Inputs
	images = input<PhotoWithDimensionsInterface[] | undefined>(undefined);
	imageVariant = input<'mini-carousel' | 'single' | 'grid'>('single');
	imageRibbon = input<string | undefined>(undefined);
	isTransparentImage = input<boolean>(false);

	// Header Inputs
	overline = input<string | undefined>(undefined);
	title = input.required<string>();
	headingType = input<HeadingType>('h3');

	// Body Inputs
	facilities = input<RoomCardFacilitiesInterface | undefined>(undefined);
	roomSurface = input<number | undefined>(undefined);
	socialProof = input<SocialProofInterface | undefined>(undefined);
	content = input<string | undefined>(undefined);
	tags = input<string[] | undefined>(undefined);

	// Pricing Inputs
	price = input<PriceInterface | undefined>(undefined);
	pricingToolTip = input<PricingTooltip | undefined>(undefined);

	// CTA Inputs
	cardUrl = input<string | undefined>(undefined);
	cardUrlQueryParams = input<Params>();
	cardLinkType = input<LinkType>('story');
	ctaUrl = input<string | undefined>(undefined);
	ctaTarget = input<LinkTarget | undefined>(undefined);
	ctaText = input<string>('global.book-directly');
	ctaType = input<'btn-primary' | 'btn-secondary'>('btn-primary');
	ctaVariant = input<'' | 'btn-outline' | 'btn-text'>('');
	ctaLinkType = input<LinkType>('story');
	secondaryCtaTarget = input<LinkTarget | undefined>(undefined);
	secondaryCtaText = input<string | undefined>(undefined);
	secondaryLinkType = input<LinkType>('story');

	// Link List Inputs
	linkListConfig = input<PersuadeCardLinkList | undefined>();

	// Layout Inputs
	reversedCtaColumn = input<boolean>(false);
	useVerticalCard = input<boolean>(false);
	showReadMore = input<boolean>(false);
	readMoreText = input<string>('global.show-more');

	showFooterArea = computed(() => {
		return (
			!!this.price() ||
			!!this.ctaUrl() ||
			this.linkListHasItems() ||
			this.hasFooterContent()
		);
	});

	pricingConditionByType = computed(() => {
		const t = this.price()?.type;
		if (t === 'perPerson') return 'global.price-per-person';
		if (t === 'perNight') return 'global.price-per-night';
		return 'global.price';
	});

	ctaPrimaryClasses = computed(
		() => `${this.ctaType()} ${this.ctaVariant()}`,
	);

	secondaryCtaVariant = computed(() =>
		this.ctaVariant() === ''
			? `${this.ctaType()} btn-outline`
			: `${this.ctaType()} btn-text`,
	);

	linkListHasItems = computed(
		() => (this.linkListConfig()?.items?.length ?? 0) > 0,
	);

	readonly isMobile$: Observable<boolean>;
	readonly isTablet$: Observable<boolean>;
	height = 0;

	carouselItems = computed(() =>
		this.images()?.map((image) => ({
			src: image.src,
			altText: image.alt,
			width: 0,
			height: 0,
		})),
	);
	@ViewChild('persuadeRef') persuadeRef!: ElementRef;

	cn = cn;

	// returns a boolean<true> when detected that <ng-content footerContent> is being used
	private readonly _hasFooterContent = signal(false);
	readonly hasFooterContent = computed(() => this._hasFooterContent());

	@ContentChild(FooterContentDirective)
	set footerSlot(dir: FooterContentDirective | undefined) {
		this._hasFooterContent.set(!!dir);
	}

	constructor() {
		this.isMobile$ = this.viewport.isMobile$;
		this.isTablet$ = this.viewport.isSmallTablet$;
	}

	ngAfterViewInit(): void {
		if (isPlatformBrowser(this.platformId)) {
			setTimeout(() => {
				this.height = this.persuadeRef.nativeElement.offsetHeight;
				this.cd.detectChanges();
			});
		}
	}
}
